import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLogout } from '../hooks/useLogout';
import { useAuthProfile } from '../hooks/useAuthProfile';
import LoginPage from './LoginPage';
import { useSelectedRestaurant } from '../context/SelectedRestaurantContext';
import { useWaiterShift } from '../context/WaiterShiftContext';
import NotificationToggle, { triggerNotificationToggle } from '../components/NotificationToggle';
import NotificationStatus from '../components/NotificationStatus';
import RestaurantSelector from '../components/RestaurantSelector';

import { ProfilePWASuggestion } from '../components/PWASuggestion';
import appPackage from '../../package.json';

export default function ProfilePage() {
  
  const { user } = useAuth();
  const { logoutWithoutConfirmation, isLoading: logoutLoading } = useLogout();
  const { 
    userRole, 
    isManager, 
    isOwner,
    isWaiter,
    isKitchen,
    storedUser
  } = useAuthProfile();
  const navigate = useNavigate();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const { shiftStatus, loading: updatingShift, toggleShift, isInTurn } = useWaiterShift();



  // Cargar preferencias de notificaciones del localStorage
  useEffect(() => {
    const savedNotificationPrefs = localStorage.getItem('notification_preferences');
    if (savedNotificationPrefs) {
      try {
        const prefs = JSON.parse(savedNotificationPrefs);
        // Aquí podrías actualizar el estado de las notificaciones si es necesario
        console.log('Preferencias de notificaciones cargadas:', prefs);
      } catch (error) {
        console.error('Error parsing notification preferences:', error);
      }
    }
  }, []);

  // Unificar fondo de la página con el color de perfil
  useEffect(() => {
    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#e9ecef';
    return () => {
      document.body.style.backgroundColor = previous;
    };
  }, []);

  const handleClientPress = () => {
    navigate('/client');
  };

  const handleAdminPress = () => {
    navigate('/admin');
  };

  const handleWaiterPress = () => {
    navigate('/waiter');
  };

  const handleWaiterTablesPress = () => {
    navigate('/waiter/tables');
  };






  const handleLogout = async () => {
    try {
      await logoutWithoutConfirmation();
    } catch (error) {
      // Eliminado console.error
    }
  };

  const handleToggleShift = async () => {
    if (!user?.uid || !selectedRestaurantId) {
      alert('Selecciona un restaurante primero en el header.');
      return;
    }
    try {
      await toggleShift(); 
      triggerNotificationToggle();
      
    } catch (e: any) {
      alert(e?.message || 'No se pudo actualizar tu estado de turno');
    }
  };

  const ProfilePageContent: React.FC = () => {
    // Si el usuario no está logueado, mostrar pantalla de login
    if (!user) {
      return (
        <LoginPage />
      );
    }

    const isWaiterOffTurn = isWaiter() && !isInTurn;

    // Si el usuario está logueado, mostrar perfil completo
    return (
      <>
         <div className="min-h-[100dvh] bg-[#e9ecef]">
           {/* Header consistente con selector y back */}
           <div className="bg-[#22B6B2] dark:bg-[#2C4358] px-4 py-3 sticky top-0 z-30">
             <div className="max-w-md mx-auto flex items-center justify-between gap-3">
               <button
                 onClick={() => navigate(-1)}
                 className="p-2 text-white hover:text-white/80 hover:bg-white/10 rounded-lg transition-colors text-2xl"
                 aria-label="Regresar"
                 title="Regresar"
               >
                 ←
               </button>
               <div className="flex-1">
                 <RestaurantSelector isHeader={true} />
               </div>
               <div className="w-10" />
             </div>
           </div>
           {/* Header de perfil con gradiente */}
           <div className="bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white pt-10 pb-8 px-4">
             <div className="max-w-md mx-auto text-center">
              {/* Avatar y nombre */}
              <div className="relative mb-6">
                <div className="w-28 h-28 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm border-4 border-white/30">
                  {(() => {
                    const avatarUrl = user?.photoURL || (storedUser as any)?.picture || (storedUser as any)?.photoURL || '';
                    return avatarUrl ? (
                    <img 
                      src={avatarUrl}
                      alt="Avatar"
                      className="w-24 h-24 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="text-5xl">👤</span>
                  );
                  })()}
                </div>
                
                {/* Badge de rol */}
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-bold border border-white/30">
                    {userRole?.id === 'owner' ? '👑 Owner' : 
                     userRole?.id === 'manager' ? '👔 Manager' : 
                     userRole?.id === 'kitchen' ? '👨‍🍳 Cocina' : 
                     userRole?.id === 'waiter' ? '🤵 Mesero' : 
                     '👤 Cliente'}
                  </span>
                </div>
              </div>
              
              <h1 className="text-2xl font-bold mb-2">
                {user?.displayName || user?.email || 'Usuario'}
              </h1>
              <p className="text-white/80 text-sm mb-4">
                {user?.email || 'correo@ejemplo.com'}
              </p>
              
              {/* Status del turno para waiters */}
              {isWaiter() && (
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full border border-white/30">
                  <span className={`w-3 h-3 rounded-full ${
                    isInTurn ? 'bg-green-400' : 'bg-gray-400'
                  }`}></span>
                  <span className="text-sm font-medium">
                    {isInTurn ? 'En turno' : 'Fuera de turno'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Contenido principal */}
          <div className="max-w-md mx-auto px-4 -mt-4 pb-8 bg-[#e9ecef]" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}>
            {/* Card de acciones rápidas */}
            <div className="bg-white rounded-3xl shadow-xl mb-6 p-6 border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-2xl">⚡</span>
                Acciones Rápidas
              </h2>
              
              <div className="grid grid-cols-2 gap-3">
                <button 
                  className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4 flex flex-col items-center hover:from-blue-100 hover:to-blue-200 transition-all duration-300 transform hover:scale-105"
                  onClick={handleClientPress}
                >
                  <span className="text-3xl mb-2">🍽️</span>
                  <span className="text-sm font-bold text-blue-800 text-center">
                    Restaurantes Visitados
                  </span>
                </button>

                {(isOwner() || isManager() || isKitchen()) && (
                  <button 
                    className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4 flex flex-col items-center hover:from-purple-100 hover:to-purple-200 transition-all duration-300 transform hover:scale-105"
                    onClick={handleAdminPress}
                  >
                    <span className="text-3xl mb-2">🏢</span>
                    <span className="text-sm font-bold text-purple-800 text-center">
                      Panel Admin
                    </span>
                  </button>
                )}

                {(isOwner() || isManager() || isWaiter()) && (
                  <button 
                    className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4 flex flex-col items-center hover:from-green-100 hover:to-green-200 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    onClick={() => { if (isWaiterOffTurn) return; handleWaiterPress(); }}
                    disabled={isWaiterOffTurn}
                  >
                    <span className="text-3xl mb-2">📋</span>
                    <span className="text-sm font-bold text-green-800 text-center">
                      Ordenes {isWaiter() ? '' : 'Mesero'}
                    </span>
                  </button>
                )}

                {(isOwner() || isManager() || isWaiter()) && (
                  <button 
                    className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl p-4 flex flex-col items-center hover:from-emerald-100 hover:to-emerald-200 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    onClick={() => { if (isWaiterOffTurn) return; handleWaiterTablesPress(); }}
                    disabled={isWaiterOffTurn}
                  >
                    <img src="/assets/images/mesita.png" alt="Mesas" className="w-8 h-8 mb-2 object-contain" />
                    <span className="text-sm font-bold text-emerald-800 text-center">
                      Mesas {isWaiter() ? '' : 'Mesero'}
                    </span>
                  </button>
                )}

                {isWaiter() && (
                  <button
                    className={`rounded-2xl p-4 flex flex-col items-center transition-all duration-300 transform hover:scale-105 border-2 ${
                      shiftStatus === 'active' 
                        ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-300' : 
                        'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300'
                    }`}
                    onClick={handleToggleShift}
                    disabled={updatingShift}
                  >
                    <span className="text-3xl mb-2">{shiftStatus === 'active' ? '🟢' : '⚪️'}</span>
                    <span className={`text-sm font-bold text-center ${
                      shiftStatus === 'active' ? 'text-green-800' : 'text-gray-800'
                    }`}>
                      {shiftStatus === 'active' ? 'En turno' : 'Fuera de turno'}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Sugerencia PWA */}
            <ProfilePWASuggestion className="mb-6" />

            {/* Sección de Notificaciones para Waiters */}
            {isWaiter() && (
              <div className="bg-white rounded-3xl shadow-xl mb-6 p-6 border border-gray-100">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-2xl">⚙️</span>
                  Notificaciones
                </h2>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <NotificationToggle size="md" />
                  </div>
                  
                  <div className="text-center">
                    <NotificationStatus showDetails={true} />
                  </div>
                </div>
              </div>
            )}

            {/* Botón de cerrar sesión */}
            <div className="bg-[#e9ecef] rounded-3xl shadow-none mb-6 p-6 border border-transparent">
              <button 
                className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-4 rounded-2xl font-bold hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleLogout}
                disabled={logoutLoading}
              >
                <span className="flex items-center justify-center gap-2">
                  <span className="text-xl">🚪</span>
                  {logoutLoading ? 'Cerrando...' : 'Cerrar sesión'}
                </span>
              </button>
            </div>

            {/* Footer - estilo unificado con AdminLayout */}
            <footer className="bg-[#e9ecef] py-6 w-full mt-8">
              <div className="max-w-md mx-auto px-4 text-gray-600">
                {/* Fila 1: Logo + marca */}
                <div className="flex items-center justify-center gap-2">
                  <img
                    src="/assets/ots/nobackground/Servicio_a_Tiempo_logo_256x256.png"
                    alt="Servicio a Tiempo"
                    className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                  />
                  <span className="text-xs sm:text-sm font-medium">
                    © {new Date().getFullYear()} Servicio a Tiempo
                  </span>
                </div>
                {/* Fila 2: enlaces + versión */}
                <div className="mt-2 flex items-center justify-center gap-3">
                  <button
                    onClick={() => navigate('/terminos')}
                    className="text-[11px] sm:text-xs font-medium text-[#22B6B2] hover:text-[#1a8f8c] underline"
                  >
                    Términos y Condiciones
                  </button>
                  <span className="text-gray-400">•</span>
                  <button
                    onClick={() => navigate('/privacidad')}
                    className="text-[11px] sm:text-xs font-medium text-[#22B6B2] hover:text-[#1a8f8c] underline"
                  >
                    Privacidad
                  </button>
                  <span className="text-gray-400">•</span>
                  <span className="text-[10px] text-gray-500">v{(appPackage as any).version}</span>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </>
    );
  };

  return <ProfilePageContent />;
} 